import { Core } from "@faire/design-tokens";
import { Row } from "@faire/web--source/slate/Layout";
import { Typography } from "@faire/web--source/slate/Typography";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import * as React from "react";
import styled, { css } from "styled-components";

const CIRCLE_SIZE = 18;
const CIRCLE_ORIGIN = CIRCLE_SIZE / 2;
const CIRCLE_WIDTH = 2;
const INNER_RADIUS = 8;
const CIRCUMFERENCE = 2 * Math.PI * INNER_RADIUS;

export const ChaptersReadDisplay: React.FC<{
  chaptersRead: number;
  totalChapters: number;
}> = ({ chaptersRead, totalChapters }) => {
  const percentRead = chaptersRead / totalChapters;
  const incompleteDashOffset = percentRead * CIRCUMFERENCE;
  const completeDashOffset = CIRCUMFERENCE - incompleteDashOffset;

  if (chaptersRead > 0 && totalChapters > 0) {
    return (
      <Row gap="1x" align="center">
        <svg height={CIRCLE_SIZE} width={CIRCLE_SIZE}>
          <ProgressCircle
            stroke={Core.border.default}
            strokeWidth={CIRCLE_WIDTH}
            fill="transparent"
            r={INNER_RADIUS}
            cx={CIRCLE_ORIGIN}
            cy={CIRCLE_ORIGIN}
            $percentRead={percentRead}
            $circumference={CIRCUMFERENCE}
            $dashOffset={completeDashOffset}
            $isCompletion
          />
          <ProgressCircle
            stroke={Core.border.muted}
            strokeWidth={CIRCLE_WIDTH}
            fill="transparent"
            r={INNER_RADIUS}
            cx={CIRCLE_ORIGIN}
            cy={CIRCLE_ORIGIN}
            $percentRead={percentRead}
            $circumference={CIRCUMFERENCE}
            $dashOffset={incompleteDashOffset}
            $isCompletion={false}
          />
        </svg>
        <Typography>
          <StrictLocalMsg
            defaultMessage="{chaptersReadNumber} of {chaptersTotalNumber} chapters completed"
            description={{
              text: "Indicating the number of chapters read in a guide",
              img: "https://cdn.faire.com/fastly/f872b4f72ab9e519a8684f1c51be51e0f86622106a05746f78fef7b0260fb40e.png",
            }}
            values={{
              chaptersReadNumber: chaptersRead,
              chaptersTotalNumber: totalChapters,
            }}
          />
        </Typography>
      </Row>
    );
  }

  return null;
};

const BASE_ROTATION = -90;
const ProgressCircle = styled.circle<{
  $circumference: number;
  $dashOffset: number;
  $isCompletion: boolean;
  $percentRead: number;
}>`
  ${({ $circumference }) => css`
    stroke-dasharray: ${$circumference} ${$circumference};
  `}
  ${({ $dashOffset }) => css`
    stroke-dashoffset: ${$dashOffset};
  `}
    ${({ $isCompletion, $percentRead }) =>
    $isCompletion
      ? css`
          transform: rotate(${BASE_ROTATION}deg);
          transform-origin: 50% 50%;
        `
      : css`
          transform: rotate(${BASE_ROTATION + $percentRead * 360}deg);
          transform-origin: 50% 50%;
        `}
`;
